'use client';

import Link from 'next/link';
import React, { memo } from 'react';
import ScreenReaderOnly from './ScreenReaderOnly';

export interface FormattedLinkProps extends React.HTMLProps<HTMLAnchorElement> {
    url?: string | null | undefined;
    displayAsButton?: boolean | null;
    displayAsButtonClassName?: string;
    isExternalUrl?: boolean | null;
    children?: React.ReactNode | React.ReactNode[];
    className?: string;
    onNavigate?: () => void;
}

const FormattedLink = ({
    url,
    displayAsButton = false,
    displayAsButtonClassName = '',
    isExternalUrl = false,
    children,
    className,
    onNavigate,
    ...rest
}: FormattedLinkProps) => {
    const { ...attrs } = rest as React.HTMLAttributes<HTMLElement>;
    const linkClasses = `${className ? className : 'font-heading'} ${displayAsButton ? displayAsButtonClassName : ''}`;

    if (isExternalUrl || url?.includes('https')) {
        // If the URL is external or contains 'https', use an anchor tag
        // to open the link in a new tab

        return (
            <a
                href={url!}
                rel="noopener noreferrer"
                target="_blank"
                className={className}
                onClick={() => {
                    // Handle any state changes before navigation
                    if (onNavigate) {
                        onNavigate();
                    }
                }}
            >
                {children}
                <ScreenReaderOnly>(opens in a new tab)</ScreenReaderOnly>
            </a>
        );
    } else if (displayAsButton) {
        return (
            <Link
                className={displayAsButtonClassName}
                {...attrs}
                href={url ?? ''}
            >
                {children}
            </Link>
        );
    } else {
        return (
            <Link
                className={linkClasses}
                href={`${url}`}
                onClick={() => {
                    // Handle any state changes before navigation
                    if (onNavigate) {
                        onNavigate();
                    }
                }}
            >
                {children}
            </Link>
        );
    }
};

export default memo(FormattedLink);
